import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import NavigationHeader from "../../components/ui/NavigationHeader";
import MainNavigation from "../../components/ui/MainNavigation";
import BreadcrumbNavigation from "../../components/ui/BreadcrumbNavigation";
import QuickActionPanel from "../../components/ui/QuickActionPanel";

import CategorySidebar from "./components/CategorySidebar";
import ProductTable from "./components/ProductTable";
import ProductFilters from "./components/ProductFilters";
import ProductDetailModal from "./components/ProductDetailModal";
import BulkActionsToolbar from "./components/BulkActionsToolbar";
import QuickStatsPanel from "./components/QuickStatsPanel";

import { getAllProductsService } from "redux/services/products_sevice";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AddProduct from "pages/add-product";
import {
  exportProductService,
  getStatisticsService,
} from "redux/services/general_service";
import Pagination from "./components/Pagination";

const ProductCatalogManagement = () => {
  const { t } = useTranslation();

  const { productList, metaProduct } = useSelector((state) => state.products);

  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [isAddProduct, setIsAddProduct] = useState(false);
  const [categorySidebarCollapsed, setCategorySidebarCollapsed] =
    useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [filters, setFilters] = useState({
    search: "",
    stockStatus: "all",
    priceRange: "all",
    sortBy: "name-asc",
    showFeatured: false,
    showDiscounted: false,
    hasImages: false,
    minStock: "",
    maxStock: "",
  });

  // Mock user data
  const user = {
    name: "Sarah Johnson",
    email: "sarah.johnson@company.com",
    avatar:
      "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
  };

  // Mock categories data
  const categories = [
    {
      id: "electronics",
      name: "Electronics",
      icon: "Smartphone",
      productCount: 145,
      children: [
        {
          id: "smartphones",
          name: "Smartphones",
          icon: "Smartphone",
          productCount: 45,
        },
        { id: "laptops", name: "Laptops", icon: "Laptop", productCount: 32 },
        {
          id: "accessories",
          name: "Accessories",
          icon: "Headphones",
          productCount: 68,
        },
      ],
    },
    {
      id: "clothing",
      name: "Clothing",
      icon: "Shirt",
      productCount: 89,
      children: [
        { id: "mens", name: "Men's Clothing", icon: "User", productCount: 42 },
        {
          id: "womens",
          name: "Women's Clothing",
          icon: "User",
          productCount: 47,
        },
      ],
    },
    {
      id: "home-garden",
      name: "Home & Garden",
      icon: "Home",
      productCount: 67,
      children: [],
    },
    {
      id: "sports",
      name: "Sports & Outdoors",
      icon: "Dumbbell",
      productCount: 54,
      children: [],
    },
    {
      id: "books",
      name: "Books",
      icon: "Book",
      productCount: 123,
      children: [],
    },
  ];

  // Mock products data
  const mockProducts = [
    {
      id: 1,
      name: "iPhone 15 Pro Max",
      description: "Latest flagship smartphone with advanced camera system",
      sku: "IPH15PM-256-TB",
      category: "Smartphones",
      price: 1199.99,
      originalPrice: 1299.99,
      stock: 25,
      image:
        "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400",
      images: [
        "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400",
        "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400",
      ],
      brand: "Apple",
      weight: 0.221,
      dimensions: "159.9 × 76.7 × 8.25 mm",
      featured: true,
      discounted: true,
      createdAt: "2025-01-15T10:30:00Z",
      updatedAt: "2025-01-20T14:22:00Z",
    },
    {
      id: 2,
      name: "MacBook Pro 16-inch",
      description:
        "Professional laptop with M3 Pro chip for demanding workflows",
      sku: "MBP16-M3P-512-SG",
      category: "Laptops",
      price: 2499.99,
      stock: 8,
      image:
        "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=400",
      images: [
        "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=400",
      ],
      brand: "Apple",
      weight: 2.16,
      dimensions: "35.57 × 24.81 × 1.68 cm",
      featured: true,
      createdAt: "2025-01-10T09:15:00Z",
      updatedAt: "2025-01-19T11:45:00Z",
    },
    {
      id: 3,
      name: "Sony WH-1000XM5 Headphones",
      description: "Industry-leading noise canceling wireless headphones",
      sku: "SNY-WH1000XM5-BLK",
      category: "Accessories",
      price: 399.99,
      stock: 0,
      image:
        "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400",
      images: [
        "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400",
      ],
      brand: "Sony",
      weight: 0.25,
      dimensions: "N/A",
      createdAt: "2025-01-12T16:20:00Z",
      updatedAt: "2025-01-18T13:30:00Z",
    },
    {
      id: 4,
      name: "Nike Air Max 270",
      description: "Comfortable running shoes with Max Air cushioning",
      sku: "NK-AM270-WHT-10",
      category: "Sports & Outdoors",
      price: 149.99,
      stock: 45,
      image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400",
      images: [
        "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400",
      ],
      brand: "Nike",
      weight: 0.8,
      dimensions: "US Size 10",
      createdAt: "2025-01-08T12:00:00Z",
      updatedAt: "2025-01-17T10:15:00Z",
    },
    {
      id: 5,
      name: "Levi's 501 Original Jeans",
      description: "Classic straight-leg jeans in vintage wash",
      sku: "LV-501-VW-32X32",
      category: "Men's Clothing",
      price: 89.99,
      stock: 67,
      image: "https://images.unsplash.com/photo-1542272604-787c3835535d?w=400",
      images: [
        "https://images.unsplash.com/photo-1542272604-787c3835535d?w=400",
      ],
      brand: "Levi's",
      weight: 0.6,
      dimensions: "32W x 32L",
      createdAt: "2025-01-05T14:45:00Z",
      updatedAt: "2025-01-16T09:20:00Z",
    },
    {
      id: 6,
      name: "KitchenAid Stand Mixer",
      description: "Professional 5-quart stand mixer for baking enthusiasts",
      sku: "KA-SM5Q-RED",
      category: "Home & Garden",
      price: 449.99,
      stock: 12,
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400",
      images: [
        "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400",
      ],
      brand: "KitchenAid",
      weight: 11.1,
      dimensions: "35.3 × 22.1 × 35.3 cm",
      createdAt: "2025-01-03T11:30:00Z",
      updatedAt: "2025-01-15T15:10:00Z",
    },
    {
      id: 7,
      name: "The Psychology of Money",
      description:
        "Timeless lessons on wealth, greed, and happiness by Morgan Housel",
      sku: "BK-POM-MH-2020",
      category: "Books",
      price: 16.99,
      stock: 156,
      image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400",
      images: [
        "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400",
      ],
      brand: "Harriman House",
      weight: 0.3,
      dimensions: "15.2 × 2.3 × 22.9 cm",
      createdAt: "2025-01-01T08:00:00Z",
      updatedAt: "2025-01-14T12:45:00Z",
    },
  ];

  const handleExport = (customerIds = null) => {
    dispatch(exportProductService()).then((response)=>{
      if(response?.payload){
        console.log(response?.payload)
        window.open(`https://alwathiq-backend.devoracompany.com/api/v1/export/products`)
      }
    })
  };

    const handlePageChange = (page) => {
      setCurrentPage(page);
      dispatch(getAllProductsService({ page: page }))/*.then((response) => {
        if (response.payload.status == 200) {
          setOrders(response.payload?.data?.data);
        }
      });*/
    };
  // Pagination
  const totalItems = metaProduct?.total;
  const totalPages = metaProduct?.last_page;
  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = [...mockProducts];

    // Category filter
    if (selectedCategory !== "all") {
      const category = categories.find((cat) => cat.id === selectedCategory);
      if (category) {
        const categoryNames = [category.name];
        if (category.children) {
          categoryNames.push(...category.children.map((child) => child.name));
        }
        filtered = filtered.filter((product) =>
          categoryNames.includes(product.category)
        );
      }
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm) ||
          product.description.toLowerCase().includes(searchTerm) ||
          product.sku.toLowerCase().includes(searchTerm)
      );
    }

    // Stock status filter
    if (filters.stockStatus !== "all") {
      switch (filters.stockStatus) {
        case "in-stock":
          filtered = filtered.filter((product) => product.stock > 10);
          break;
        case "low-stock":
          filtered = filtered.filter(
            (product) => product.stock > 0 && product.stock <= 10
          );
          break;
        case "out-of-stock":
          filtered = filtered.filter((product) => product.stock === 0);
          break;
      }
    }

    // Price range filter
    if (filters.priceRange !== "all") {
      const [min, max] = filters.priceRange.split("-").map(Number);
      if (max) {
        filtered = filtered.filter(
          (product) => product.price >= min && product.price <= max
        );
      } else {
        filtered = filtered.filter((product) => product.price >= min);
      }
    }

    // Feature filters
    if (filters.showFeatured) {
      filtered = filtered.filter((product) => product.featured);
    }

    if (filters.showDiscounted) {
      filtered = filtered.filter((product) => product.discounted);
    }

    // Stock range filters
    if (filters.minStock) {
      filtered = filtered.filter(
        (product) => product.stock >= parseInt(filters.minStock)
      );
    }

    if (filters.maxStock) {
      filtered = filtered.filter(
        (product) => product.stock <= parseInt(filters.maxStock)
      );
    }

    // Sort products
    const [sortKey, sortDirection] = filters.sortBy.split("-");
    filtered.sort((a, b) => {
      let aValue = a[sortKey];
      let bValue = b[sortKey];

      if (sortKey === "created") {
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [mockProducts, selectedCategory, filters, categories]);

  // Pagination
 // const totalPages = Math.ceil(metaProduct?.total / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Statistics
  const stats = {
    totalProducts: mockProducts.length,
    lowStockItems: mockProducts.filter((p) => p.stock > 0 && p.stock <= 10)
      .length,
    outOfStockItems: mockProducts.filter((p) => p.stock === 0).length,
    totalCategories: categories.length,
  };

  // Event handlers
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleProductSelect = (productId, selected) => {
    if (selected) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      setSelectedProducts(selectedProducts.filter((id) => id !== productId));
    }
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      setSelectedProducts(paginatedProducts.map((p) => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleProductView = (product) => {
    console.log(product);
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  const handleProductEdit = (productId, updates) => {
    console.log("Edit product:", productId, updates);
    // In a real app, this would update the product in the backend
  };

  const handleSort = (column) => {
    const direction =
      sortConfig.key === column && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ key: column, direction });

    // Update filters to match
    setFilters({
      ...filters,
      sortBy: `${column}-${direction}`,
    });
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      stockStatus: "all",
      priceRange: "all",
      sortBy: "name-asc",
      showFeatured: false,
      showDiscounted: false,
      hasImages: false,
      minStock: "",
      maxStock: "",
    });
    setSelectedCategory("all");
    setCurrentPage(1);
  };

  const handleBulkAction = (actionData) => {
    console.log("Bulk action:", actionData, "on products:", selectedProducts);
    // In a real app, this would perform the bulk action
    setSelectedProducts([]);
  };

  const handleClearSelection = () => {
    setSelectedProducts([]);
  };

  const handleAddProduct = () => {
    console.log("Add new product");
    setIsAddProduct(true);
    // window.location.href = "/add-product";
    // In a real app, this would open a product creation form
  };

  // Check for action parameter
  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "create") {
      handleAddProduct();
      // Clear the action parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    dispatch(getAllProductsService({ page: 1 }));
    dispatch(getStatisticsService());
  }, []);

  return (
    <div className="min-h-screen bg-surface">
      {isAddProduct ? (
        <AddProduct setIsAddProduct={setIsAddProduct} />
      ) : (
        <div className="flex">
          {/* Main Content */}
          <div className="flex-1 p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {t("productCatalogManagement")}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {t("manageYourProducts")}
                </p>
              </div>

              {/* Export Button */}
              <div className="flex item-center gap-2">
                {" "}
                <Button
                  variant="outline"
                  onClick={handleExport}
                  //   className="flex items-center"
                >
                  <Icon name="Download" size={16} />
                  <span className="hidden sm:inline pr-2 pl-2">
                    {t("export")}
                  </span>
                </Button>
                <Button
                  variant="default"
                  onClick={handleAddProduct}
                  iconName="Plus"
                  iconPosition="left"
                >
                  {t("addProduct")}
                </Button>
              </div>
            </div>

            {/* Quick Stats */}
            <QuickStatsPanel stats={stats} />

            {/* Filters 
            <ProductFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
            />*/}

            {/* Products Table */}
            <div className="mt-6">
              <ProductTable
                products={productList}
                selectedProducts={selectedProducts}
                onProductSelect={handleProductSelect}
                onSelectAll={handleSelectAll}
                onProductEdit={handleProductEdit}
                onProductView={handleProductView}
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>

            {/* Pagination */}

               <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
            {/*metaProduct?.total > 9 && (
              <div className="flex justify-center py-4 space-x-2">
                <button
                  className="px-4 py-2 border rounded-md select-none hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    setCurrentPage(currentPage - 1);
                    dispatch(getAllProductsService({ page: currentPage - 1 }));
                  }}
                  disabled={currentPage === 1}
                >
                  {t("previous")}
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={i}
                      className={`px-4 py-2 rounded-md select-none border ${
                        page === currentPage
                          ? "bg-green-200 border-green-400 text-green-900"
                          : "border-gray-300 hover:bg-green-50"
                      }`}
                      onClick={() => {
                        setCurrentPage(page);
                        dispatch(getAllProductsService({ page: page }));
                      }}
                      //    onClick={() => onPageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  className="px-4 py-2 border rounded-md select-none hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    setCurrentPage(currentPage + 1);
                    dispatch(getAllProductsService({ page: currentPage + 1 }));
                  }}
                  disabled={currentPage === totalPages}
                >
                  {t("next")}
                </button>
              </div>
            )*/}
            {/*<div className="flex items-center justify-between mt-6">
           
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCurrentPage(currentPage - 1);
                        dispatch(
                          getAllProductsService({ page: currentPage - 1 })
                        );
                      }}
                      disabled={currentPage === 1}
                      iconName="ChevronLeft"
                      iconPosition="left"
                    >
                      Previous
                    </Button>

                    <div className="flex items-center space-x-1">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          const page = i + 1;
                          return (
                            <Button
                              key={page}
                              variant={
                                currentPage === page ? "default" : "outline"
                              }
                              onClick={() => {
                                dispatch(getAllProductsService({ page: page }));
                                setCurrentPage(page);
                              }}
                              className="w-10 h-10"
                            >
                              {page}
                            </Button>
                          );
                        }
                      )}
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => {
                        setCurrentPage(currentPage + 1);
                        dispatch(
                          getAllProductsService({ page: currentPage + 1 })
                        );
                      }}
                      disabled={currentPage === totalPages}
                      iconName="ChevronRight"
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  </div>
                </div>*/}
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={showProductModal}
        onClose={() => {
          setShowProductModal(false);
          setSelectedProduct(null);
        }}
        onEdit={(product) => {
          console.log("Edit product from modal:", product);
          setShowProductModal(false);
        }}
        onDelete={(product) => {
          alert(product);
          console.log("Delete product:", product);
          setShowProductModal(false);
        }}
      />
    </div>
  );
};

export default ProductCatalogManagement;
